/**
 * When a Pro booking happens, in the reader's own locale and zone. A one-session offer used to
 * print its date only ("Nov 7"), so a 9am lesson and an 8pm reception read the same — testers
 * could not tell when they were booked (Nov 2 2026). The date comes with the start time, and the
 * end time when the offer has one.
 */

const sameLocalDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Sat, Nov 7" (+ ", 2026" with `year`). */
export const bookingDay = (iso, { year = false, weekday = true } = {}) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { ...(weekday ? { weekday: 'short' } : {}), month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) })
  : null);

/** "6:00 PM" / "18:00", whichever the reader's locale writes. */
export const bookingClock = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : null);

/**
 * "Sat, Nov 7 · 6:00 PM – 8:00 PM". An end on another day carries its own date. With no start
 * time (an open-ended offer, or one made before times were required) it falls back to the date.
 */
export function bookingWhen({ startsAt, endsAt, eventDate } = {}, opts = {}) {
  if (startsAt) {
    const start = new Date(startsAt);
    const head = `${bookingDay(startsAt, opts)} · ${bookingClock(startsAt)}`;
    if (!endsAt) return head;
    const end = new Date(endsAt);
    if (!(end > start)) return head;
    return sameLocalDay(start, end)
      ? `${head} – ${bookingClock(endsAt)}`
      : `${head} – ${bookingDay(endsAt, opts)} · ${bookingClock(endsAt)}`;
  }
  return eventDate ? bookingDay(eventDate, opts) : null;
}

/**
 * Every session of an offer, in order: a package's own list, else the one window the offer
 * carries, else its bare date. Empty when it has none of them.
 */
export function offerSessions(offer) {
  if (!offer) return [];
  if (Array.isArray(offer.sessions) && offer.sessions.length > 0) {
    return [...offer.sessions].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  }
  if (offer.startsAt) return [{ number: 1, startsAt: offer.startsAt, endsAt: offer.endsAt || null }];
  if (offer.eventDate) return [{ number: 1, startsAt: null, endsAt: null, eventDate: offer.eventDate }];
  return [];
}
